import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import DeliveryBoyForm from '../components/DeliveryBoyForm';
import { createDeliveryBoy } from '../api/index';
import { DeliveryBoyFormData } from '../types';
import { FiArrowLeft, FiSave } from 'react-icons/fi';

const AddDeliveryBoy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (formData: DeliveryBoyFormData): Promise<void> => {
    setLoading(true);
    setSuccessMessage('');
    
    try {
      const newDeliveryBoy = await createDeliveryBoy(formData);
      setSuccessMessage(`Delivery boy ${newDeliveryBoy.firstName} ${newDeliveryBoy.lastName} has been created successfully!`);
      
      // Navigate to list after a brief delay to show success message
      setTimeout(() => {
        navigate('/admin/delivery-boys');
      }, 2000);
    } catch (error) {
      console.error('Error creating delivery boy:', error);
      // Error handling would be done in the form component
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/delivery-boys')}
            className="flex items-center space-x-2"
          >
            <FiArrowLeft className="h-4 w-4" />
            <span>{t('action.back')}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{t('sidebar.deliveryBoys')}</h1>
            <p className="text-slate-600 dark:text-slate-300">{t('action.create')}</p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Card className="border-green-200 bg-green-50">
          <div className="flex items-center space-x-2 text-green-800">
            <FiSave className="h-5 w-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </Card>
      )}

      {/* Form */}
      <Card>
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Delivery Boy Information</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Please fill in all required information to create a new delivery boy profile.
            </p>
          </div>

          <DeliveryBoyForm
            onSubmit={handleSubmit}
            loading={loading}
          />
        </div>
      </Card>

      {/* Help Text */}
      <Card className="border-blue-200 bg-blue-50">
        <div className="p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• All fields marked with * are required</li>
            <li>• Phone number must be valid and unique</li>
            <li>• Email address must be valid and unique</li>
            <li>• License number must be valid for the selected vehicle type</li>
            <li>• Bank account details will be used for salary payments</li>
            <li>• Upload clear photos of required documents</li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default AddDeliveryBoy;